import styled, { css } from 'styled-components';
import { IInputProps } from '@template/components/Input/index.tsx';
import {
  StandardLink,
  NextLink,
} from '@template/components/StyledLink/styles.ts';

export const InputContent = styled.p``;

export const InputLabel = styled.p<{
  $labelPosition?: IInputProps['labelPosition'];
}>`
  color: ${({ theme }) => theme.colors.default[900]};
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  cursor: default;
  white-space: nowrap;
  max-width: 100%;
  text-overflow: ellipsis;
  overflow: clip;
  display: flex;
  gap: 1px;
  align-items: center;
  ${({ $labelPosition }) => {
    switch ($labelPosition) {
      case 'inside-top':
        return css`
          color: ${({ theme }) => theme.colors.default[400]};
          padding: 10px 14px 0 14px;
          margin-bottom: -8px;
        `;
      case 'inside':
        return css`
          color: ${({ theme }) => theme.colors.default[400]};
          padding: 0 0 0 14px;
          margin-right: -8px;
        `;
      default:
        return css``;
    }
  }}
`;

export const RequiredStar = styled.span`
  color: ${({ theme }) => theme.colors.primary.default};
  font-weight: 600;
`;

export const InputContainer = styled.div`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.default[200]};
  border-radius: 4px;
  max-width: 300px;
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
  display: flex;
  transition: 0.2s ease-out;
  flex-direction: column;
  overflow: clip;
  background: ${({ theme }) => theme.colors.base};

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.default[900]};
    box-shadow: 0 0 0 4px ${({ theme }) => theme.colors.outline};
  }
`;

export const Prefix = styled.p`
  padding: 10px 14px;
  border-right: 1px solid ${({ theme }) => theme.colors.default[200]};
  width: fit-content;
  color: ${({ theme }) => theme.colors.default[400]};
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  text-wrap: nowrap;
  display: flex;
  align-items: center;
  flex: 1;
  height: 100%;
  min-height: 100%;
`;

export const Suffix = styled(Prefix)`
  border-right: none;
  border-left: 1px solid ${({ theme }) => theme.colors.default[200]};
`;

export const InputContentContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 14px;
  gap: 8px;
  color: ${({ theme }) => theme.colors.default[400]};
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  width: 100%;
  height: 100%;
`;

export const Input = styled.input`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.default[900]};
  cursor: text;
  text-overflow: ellipsis;
  flex: 1;
  max-width: 100%;
  width: 100%;

  &::placeholder {
    color: ${({ theme }) => theme.colors.default[300]};
  }

  &:focus {
    outline: none;
  }

  &:-webkit-autofill {
    -webkit-box-shadow: 0 0 0 30px transparent inset !important;
    -webkit-text-fill-color: currentColor !important;
    font-size: 16px;
    cursor: text;
    flex: 1;
    max-width: 100%;
    width: 100%;
  }

  &::-webkit-file-upload-button {
    visibility: hidden;
    display: none;
  }

  &[data-type='file'] {
    cursor: pointer;

    &[data-has-value='true'] {
    }

    &[data-has-value='false'] {
      color: ${({ theme }) => theme.colors.default[300]};
    }
  }
`;

export const TextArea = styled.textarea`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.default[900]};
  cursor: text;
  width: 100%;
  text-overflow: ellipsis;
  resize: none;
  height: 128px;
  background: transparent;
  outline: none;
  border: none;
  font-family: inherit !important;
  line-height: inherit;

  &::placeholder {
    color: ${({ theme }) => theme.colors.default[300]};
  }
`;

export const Select = styled.select`
  color: ${({ theme }) => theme.colors.default[900]};
  font-size: 16px;
  width: 100%;

  &::placeholder {
    color: ${({ theme }) => theme.colors.default[400]};
  }

  &:focus {
    outline: none;
  }
`;

export const Hint = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.default[600]};
  line-height: 20px;

  a {
    color: ${({ theme }) => theme.colors.default[600]};
    text-decoration: underline;
  }
`;

export const Error = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.secondary[400]};
  line-height: 20px;
`;

export const Inline = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  position: relative;
  min-width: 150px;
`;

export const InputWrapper = styled.div<{
  $disabled?: boolean;
  $error?: boolean;
  $fullWidth?: boolean;
  $variant?: IInputProps['variant'];
}>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: relative;
  width: max-content;
  height: max-content;

  ${({ $disabled }) =>
    $disabled &&
    css`
      ${InputContainer} {
        background: ${({ theme }) => theme.colors.default[50]};
      }
    `}

  ${({ $error }) =>
    $error &&
    css`
      ${InputContainer} {
        border-color: ${({ theme }) => theme.colors.secondary[300]};

        &:focus-within {
          box-shadow: 0 0 0 4px ${({ theme }) => theme.colors.secondary[100]};
        }
      }
    `}

    ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      width: 100%;

      ${InputContainer} {
        max-width: revert;
      }
    `}

    ${({ theme, $variant }) => {
    switch ($variant) {
      case 'light':
        return css`
          ${InputContainer} {
            background: transparent;
            border: none;
            border-bottom: 1px solid ${({ theme }) => theme.colors.default[100]};
            border-radius: 0;

            ${InputContentContainer} {
              padding: 5px 0;
            }

            &:not(:focus-within) {
              box-shadow: none;
            }
          }
        `;
      case 'minimal':
        return css`
          ${InputContainer} {
            background: transparent;
            border: none;
            ${({ theme }) => theme.colors.default[100]};
            border-radius: 0;

            ${InputContentContainer} {
              padding: 5px 0;
            }

            &:not(:focus-within) {
              box-shadow: none;
            }
          }
        `;
      case 'solid':
      default:
        return css``;
    }
  }}
`;

export const ColorCircle = styled.button.attrs(({ $color }: any) => ({
  style: {
    background: `#${$color}`,
  },
}))<{ $color?: string }>`
  width: 24px;
  height: auto;
  min-width: 24px;
  border-radius: 6px;
  aspect-ratio: 1;
  border: 1px solid ${({ theme }) => theme.colors.default[300]};
  font-size: 10px;
  color: ${({ theme }) => theme.colors.default.default};
`;

export const ImageDrop = styled(Input)`
  aspect-ratio: 1;
  height: auto;
  background: red;
`;
